import { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Trophy, Sparkles, Check, X, RotateCw, ArrowRight, Target } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CHALLENGE_TOPICS } from '@/data/challengesData';
import { LANGUAGE_WHEEL_QUESTIONS } from '@/data/i18n/wheelQuestions';
import { useCourseLanguage, getLangInfo } from '@/data/languages';
import { logActivity, getLearnerId } from '@/utils/api';

// Phase machine: 'idle' | 'spinning' | 'landed' | 'quiz' | 'result'

const WHEEL_SIZE = 380;
const RADIUS = WHEEL_SIZE / 2;

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
};

const SpinningWheel = () => {
  const navigate = useNavigate();
  const learnerId = getLearnerId();

  const [phase, setPhase] = useState('idle');
  const [rotation, setRotation] = useState(0);
  const [landedTopic, setLandedTopic] = useState(null);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0); // total attempts in current run
  const [showResult, setShowResult] = useState(false);
  const rotationRef = useRef(0);
  const [lang] = useCourseLanguage();

  const topics = useMemo(() => {
    const info = getLangInfo(lang);
    const pool = [...LANGUAGE_WHEEL_QUESTIONS[lang]].sort(() => Math.random() - 0.5).slice(0, 5);
    return [...CHALLENGE_TOPICS, { id: `${lang}-essentials`, label: `${info.label} Essentials`, color: info.color, emoji: '💻', questions: pool }];
  }, [lang]);

  const sliceAngle = 360 / topics.length;

  const slices = useMemo(() => {
    return topics.map((topic, i) => {
      const startAngle = i * sliceAngle;
      const endAngle = (i + 1) * sliceAngle;
      const mid = (startAngle + endAngle) / 2;
      const textPos = polarToCartesian(RADIUS, RADIUS, RADIUS * 0.62, mid);
      return {
        topic,
        path: describeArc(RADIUS, RADIUS, RADIUS - 4, startAngle, endAngle),
        textX: textPos.x,
        textY: textPos.y,
        mid,
      };
    });
  }, [sliceAngle, topics]);

  const spin = () => {
    if (phase === 'spinning') return;
    // Reset quiz state
    setSelected(null);
    setAnswered(false);
    setQuestionIdx(0);
    setScore(0);
    setShowResult(false);

    const target = Math.floor(Math.random() * topics.length);
    const targetMidAngle = target * sliceAngle + sliceAngle / 2;
    // Pointer sits at top (angle 0). We want target slice center at 0 => rotate by -targetMidAngle.
    // Add 5 full spins for drama.
    const spins = 5 + Math.floor(Math.random() * 3);
    const base = rotationRef.current - (rotationRef.current % 360);
    const finalRotation = base + spins * 360 + (360 - targetMidAngle);
    rotationRef.current = finalRotation;
    setRotation(finalRotation);
    setPhase('spinning');

    setTimeout(() => {
      setLandedTopic(topics[target]);
      setPhase('landed');
    }, 4200);
  };

  const startQuiz = () => {
    setPhase('quiz');
  };

  const submitAnswer = () => {
    if (selected === null || answered) return;
    const q = landedTopic.questions[questionIdx];
    const isCorrect = selected === q.correctIndex;
    setAnswered(true);
    setAttempts(a => a + 1);
    if (isCorrect) setScore(s => s + 1);
  };

  const nextQuestion = async () => {
    const isLast = questionIdx === landedTopic.questions.length - 1;
    if (isLast) {
      // Log activity
      const finalScore = score;
      const total = landedTopic.questions.length;
      if (learnerId) {
        await logActivity('challenge_completed', landedTopic.id, finalScore, total);
      }
      setShowResult(true);
      setPhase('result');
    } else {
      setQuestionIdx(i => i + 1);
      setSelected(null);
      setAnswered(false);
    }
  };

  const spinAgain = () => {
    setPhase('idle');
    setLandedTopic(null);
    setSelected(null);
    setAnswered(false);
    setQuestionIdx(0);
    setScore(0);
    setShowResult(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Header />

      <main
        data-testid="challenges-page"
        className="pt-24 pb-12 max-w-6xl mx-auto px-6 flex-1 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFB800]/10 to-[#EC4899]/10 border border-[#FFB800]/30 rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-[#FFB800]" />
            <span className="text-[#FFB800] text-xs font-code tracking-widest">SPIN · LEARN · WIN</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3 tracking-tight">
            DSA <span className="bg-gradient-to-r from-[#FFB800] to-[#EC4899] bg-clip-text text-transparent">Challenge Wheel</span>
          </h1>
          <p className="text-[#A1A1AA] font-body max-w-xl mx-auto">
            Spin the wheel to pick a random DSA topic — then survive 5 rapid-fire MCQs.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[auto_1fr] gap-10 items-start">
          {/* WHEEL */}
          <div className="mx-auto lg:mx-0 relative" style={{ width: WHEEL_SIZE, height: WHEEL_SIZE + 40 }}>
            {/* Pointer */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20 pointer-events-none">
              <div
                className="w-0 h-0"
                style={{
                  borderLeft: '14px solid transparent',
                  borderRight: '14px solid transparent',
                  borderTop: '26px solid #FFB800',
                  filter: 'drop-shadow(0 4px 8px rgba(255,184,0,0.6))',
                }}
              />
            </div>

            {/* Glow */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(255,184,0,0.15) 0%, transparent 70%)',
                filter: 'blur(20px)',
              }}
            />

            <motion.svg
              data-testid="wheel-svg"
              width={WHEEL_SIZE}
              height={WHEEL_SIZE}
              viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}
              animate={{ rotate: rotation }}
              transition={{ duration: 4, ease: [0.17, 0.67, 0.16, 1.0] }}
              style={{ transformOrigin: 'center' }}
              className="relative z-10 drop-shadow-2xl"
            >
              {/* Outer ring */}
              <circle cx={RADIUS} cy={RADIUS} r={RADIUS - 2} fill="#0F0F12" stroke="#FFB800" strokeWidth="4" />

              {slices.map(({ topic, path, textX, textY, mid }, i) => (
                <g key={topic.id}>
                  <path d={path} fill={topic.color} opacity="0.9" stroke="#050505" strokeWidth="2" />
                  <g transform={`translate(${textX}, ${textY}) rotate(${mid})`}>
                    <text
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#050505"
                      fontSize="28"
                      style={{ userSelect: 'none' }}
                    >
                      {topic.emoji}
                    </text>
                    <text
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#050505"
                      fontSize="12"
                      fontWeight="700"
                      fontFamily="'JetBrains Mono', monospace"
                      y="26"
                      style={{ userSelect: 'none' }}
                    >
                      {topic.label.toUpperCase()}
                    </text>
                  </g>
                </g>
              ))}

              {/* Center hub */}
              <circle cx={RADIUS} cy={RADIUS} r="26" fill="#050505" stroke="#FFB800" strokeWidth="3" />
              <circle cx={RADIUS} cy={RADIUS} r="8" fill="#FFB800" />
            </motion.svg>

            <Button
              data-testid="spin-button"
              onClick={spin}
              disabled={phase === 'spinning' || phase === 'quiz'}
              className="mt-6 w-full bg-gradient-to-r from-[#FFB800] to-[#FF8800] hover:from-[#FFC820] hover:to-[#FF9820] text-black font-heading font-bold py-6 text-lg shadow-[0_0_40px_rgba(255,184,0,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {phase === 'spinning' ? (
                <>
                  <RotateCw className="w-5 h-5 mr-2 animate-spin" /> Spinning...
                </>
              ) : phase === 'quiz' ? (
                <>Quiz in progress</>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" /> SPIN THE WHEEL
                </>
              )}
            </Button>
          </div>

          {/* SIDE PANEL — Landed / Quiz / Result */}
          <div className="min-h-[500px]">
            <AnimatePresence mode="wait">
              {phase === 'idle' && (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="w-6 h-6 text-[#FFB800]" />
                    <h2 className="font-heading text-2xl font-bold text-white">How it works</h2>
                  </div>
                  <ol className="space-y-3 text-[#A1A1AA] font-body">
                    <li className="flex gap-3">
                      <span className="text-[#FFB800] font-bold font-code">01</span>
                      Tap <span className="text-white">SPIN THE WHEEL</span> — the wheel picks a random DSA topic.
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#FFB800] font-bold font-code">02</span>
                      Answer <span className="text-white">5 MCQs</span> on that topic — instant feedback, real explanations.
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#FFB800] font-bold font-code">03</span>
                      Your score is logged — <span className="text-white">Kapil sees it live</span> on the admin dashboard.
                    </li>
                  </ol>
                  <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                    {topics.map((t) => (
                      <div key={t.id} className="flex flex-col items-center gap-1">
                        <span className="text-2xl">{t.emoji}</span>
                        <span className="text-white text-xs font-code">{t.label}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {phase === 'landed' && landedTopic && (
                <motion.div
                  key="landed"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-[#0F0F12] border-2 rounded-2xl p-8 text-center"
                  style={{ borderColor: landedTopic.color + '80', boxShadow: `0 0 40px ${landedTopic.color}30` }}
                  data-testid="landed-panel"
                >
                  <div className="text-6xl mb-3">{landedTopic.emoji}</div>
                  <div className="text-[#71717A] font-code text-xs tracking-widest mb-2">YOU LANDED ON</div>
                  <h2 className="font-heading text-4xl font-bold mb-6" style={{ color: landedTopic.color }}>
                    {landedTopic.label}
                  </h2>
                  <p className="text-[#A1A1AA] font-body mb-8">
                    Get ready for <span className="text-white font-bold">5 rapid-fire MCQs</span> on {landedTopic.label}. No timer, no pressure — just learn.
                  </p>
                  <Button
                    data-testid="start-quiz-button"
                    onClick={startQuiz}
                    className="bg-white text-black hover:bg-white/90 font-heading font-bold px-8 py-5 text-base"
                  >
                    Start Quiz <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              )}

              {phase === 'quiz' && landedTopic && (
                <motion.div
                  key={`quiz-${questionIdx}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8"
                  data-testid="quiz-panel"
                >
                  {/* Progress */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{landedTopic.emoji}</span>
                      <div>
                        <div className="text-white font-heading font-semibold">{landedTopic.label}</div>
                        <div className="text-[#71717A] text-xs font-code">
                          Question {questionIdx + 1} of {landedTopic.questions.length}
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30 border font-code">
                      Score: {score}/{landedTopic.questions.length}
                    </Badge>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-6">
                    <motion.div
                      className="h-full"
                      style={{ background: landedTopic.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${((questionIdx + 1) / landedTopic.questions.length) * 100}%` }}
                    />
                  </div>

                  <h3 className="font-heading text-xl font-semibold text-white mb-6 leading-snug">
                    {landedTopic.questions[questionIdx].q}
                  </h3>

                  <div className="space-y-3 mb-6">
                    {landedTopic.questions[questionIdx].options.map((opt, i) => {
                      const isCorrect = i === landedTopic.questions[questionIdx].correctIndex;
                      const isSelected = selected === i;
                      let cls =
                        'w-full text-left px-4 py-3 rounded-lg border font-body transition-all duration-200';
                      if (!answered) {
                        cls += isSelected
                          ? ' border-[#FFB800] bg-[#FFB800]/10 text-white'
                          : ' border-white/10 bg-[#1A1A20] text-[#A1A1AA] hover:border-white/30 hover:text-white';
                      } else if (isCorrect) {
                        cls += ' border-green-500 bg-green-500/10 text-green-400';
                      } else if (isSelected) {
                        cls += ' border-red-500 bg-red-500/10 text-red-400';
                      } else {
                        cls += ' border-white/10 bg-[#1A1A20] text-[#71717A]';
                      }
                      return (
                        <button
                          key={i}
                          data-testid={`quiz-option-${i}`}
                          disabled={answered}
                          onClick={() => setSelected(i)}
                          className={cls}
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full border border-current flex items-center justify-center text-xs font-code">
                              {String.fromCharCode(65 + i)}
                            </span>
                            <span className="flex-1">{opt}</span>
                            {answered && isCorrect && <Check className="w-5 h-5" />}
                            {answered && isSelected && !isCorrect && <X className="w-5 h-5" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {answered && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-lg p-4 mb-6 border ${
                        selected === landedTopic.questions[questionIdx].correctIndex
                          ? 'bg-green-500/5 border-green-500/30 text-green-300'
                          : 'bg-red-500/5 border-red-500/30 text-red-300'
                      }`}
                    >
                      <div className="font-code text-xs uppercase tracking-wider mb-1 opacity-70">
                        {selected === landedTopic.questions[questionIdx].correctIndex ? 'Correct!' : 'Not quite'}
                      </div>
                      <div className="font-body text-sm leading-relaxed">
                        {landedTopic.questions[questionIdx].explanation}
                      </div>
                    </motion.div>
                  )}

                  {!answered ? (
                    <Button
                      data-testid="submit-answer-button"
                      onClick={submitAnswer}
                      disabled={selected === null}
                      className="w-full bg-[#FFB800] hover:bg-[#FFC820] text-black font-heading font-bold py-5 disabled:opacity-40"
                    >
                      Submit Answer
                    </Button>
                  ) : (
                    <Button
                      data-testid="next-question-button"
                      onClick={nextQuestion}
                      className="w-full bg-white text-black hover:bg-white/90 font-heading font-bold py-5"
                    >
                      {questionIdx === landedTopic.questions.length - 1 ? 'See Results' : 'Next Question'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  )}
                </motion.div>
              )}

              {phase === 'result' && landedTopic && showResult && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8 text-center"
                  data-testid="result-panel"
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', damping: 12 }}
                    className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{
                      background:
                        score === landedTopic.questions.length
                          ? 'linear-gradient(135deg, #FFB800, #FF8800)'
                          : score >= 3
                          ? 'linear-gradient(135deg, #007AFF, #0055CC)'
                          : 'linear-gradient(135deg, #71717A, #52525B)',
                    }}
                  >
                    <Trophy className="w-12 h-12 text-white" />
                  </motion.div>
                  <div className="text-[#71717A] font-code text-xs tracking-widest mb-2">
                    {landedTopic.label.toUpperCase()} · CHALLENGE COMPLETE
                  </div>
                  <h2 className="font-heading text-5xl font-bold text-white mb-2">
                    {score}<span className="text-[#71717A]">/{landedTopic.questions.length}</span>
                  </h2>
                  <p className="text-[#A1A1AA] font-body mb-8">
                    {score === landedTopic.questions.length
                      ? 'Flawless! You crushed it. 🔥'
                      : score >= 3
                      ? 'Nice work — you\'ve got the fundamentals.'
                      : 'Every miss is a lesson. Spin again!'}
                  </p>

                  <div className="flex gap-3 justify-center flex-wrap">
                    <Button
                      data-testid="spin-again-button"
                      onClick={spinAgain}
                      className="bg-gradient-to-r from-[#FFB800] to-[#FF8800] text-black hover:from-[#FFC820] hover:to-[#FF9820] font-heading font-bold px-6"
                    >
                      <RotateCw className="w-4 h-4 mr-2" /> Spin Again
                    </Button>
                    <Button
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5 font-heading"
                    >
                      Back to Lessons
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SpinningWheel;
